import React, { useState, useEffect } from 'react';
import './GestionBulletins.css';
import DashboardHeader from '../../components/DashboardHeader';
import AdminNavigationBar from '../../components/AdminNavigationBar';

function GestionBulletins() {
  const [bulletins, setBulletins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedBulletin, setSelectedBulletin] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [statistiques, setStatistiques] = useState(null);
  const [filtres, setFiltres] = useState({
    classe: '',
    trimestre: '',
    annee_scolaire: '2025-2026'
  });

  const chargerBulletins = async () => {
    if (!filtres.classe || !filtres.trimestre) {
      setBulletins([]);
      setStatistiques(null);
      return;
    }

    try {
      setLoading(true);
      const response = await fetch('http://127.0.0.1:8000/api/v1/admin/notes/previsualiser-bulletins-classe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(filtres)
      });
      
      const data = await response.json();
      console.log('Bulletins prévisualisés:', data);
      
      if (response.ok && data.success) {
        setBulletins(data.bulletins || []);
        setStatistiques(data.statistiques || null);
      } else {
        console.error('Erreur API:', data);
        setBulletins([]);
        setStatistiques(null);
      }
    } catch (error) {
      console.error('Erreur chargement bulletins:', error);
      setBulletins([]);
      setStatistiques(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    chargerBulletins();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtres]);

  const ouvrirModal = (bulletin) => {
    setSelectedBulletin(bulletin);
    setShowModal(true);
  };

  const fermerModal = () => {
    setShowModal(false);
    setSelectedBulletin(null);
  };

  const genererBulletinIndividuel = async (etudiantId) => {
    if (!window.confirm('Voulez-vous générer le bulletin pour cet élève ?')) {
      return;
    }

    try {
      const response = await fetch('http://127.0.0.1:8000/api/v1/admin/bulletins/generer-individuel', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          etudiant_id: etudiantId,
          trimestre: filtres.trimestre,
          annee_scolaire: filtres.annee_scolaire
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        alert('✅ Bulletin généré avec succès!');
        chargerBulletins();
        fermerModal();
      } else {
        alert('❌ Erreur: ' + (data.message || 'Erreur inconnue'));
      }
    } catch (error) {
      console.error('Erreur génération:', error);
      alert('❌ Erreur de connexion');
    }
  };

  const genererTousLesBulletins = async () => {
    if (!statistiques || !statistiques.peut_generer_classe) {
      alert('❌ Impossible de générer tous les bulletins.\nCertains élèves ont des notes manquantes.');
      return;
    }

    if (!window.confirm(
      `Voulez-vous générer les bulletins pour toute la classe?\n\n` +
      `📊 ${statistiques.total_eleves} élève(s)\n` +
      `✅ ${statistiques.bulletins_complets} bulletin(s) complet(s)`
    )) {
      return;
    }

    try {
      const response = await fetch('http://127.0.0.1:8000/api/v1/admin/notes/generer-bulletins', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(filtres)
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        alert(`✅ Génération terminée!\n\n${data.bulletins_generes} bulletin(s) généré(s)`);
        chargerBulletins();
      } else {
        alert('❌ Erreur: ' + (data.message || 'Erreur inconnue'));
      }
    } catch (error) {
      console.error('Erreur génération:', error);
      alert('❌ Erreur de connexion');
    }
  };

  const getStatutBadge = (bulletin) => {
    if (bulletin.est_complet) {
      return <span className="badge badge-success">✅ Complet</span>;
    } else {
      return <span className="badge badge-warning">⚠️ Partiel</span>;
    }
  };

  return (
    <div className="admin-layout">
      <DashboardHeader />
      <AdminNavigationBar 
        title="Gestion des Bulletins" 
        onBack={() => window.navigateTo && window.navigateTo('admin-dashboard')}
      />
      <div className="admin-container">
        <main className="admin-content admin-content-full">
          <div className="bulletins-container">
            <div className="bulletins-content">
              
              {/* En-tête */}
              <div className="page-header">
                <div className="header-content">
                  <div className="header-text">
                    <h1>📋 Gestion des Bulletins</h1>
                    <p>Prévisualisez et générez les bulletins scolaires</p>
                  </div>
                  {statistiques && (
                    <div className="header-stats">
                      <div className="stat-item">
                        <span className="stat-number">{statistiques.total_eleves}</span>
                        <span className="stat-label">Élèves</span>
                      </div>
                      <div className="stat-item success">
                        <span className="stat-number">{statistiques.bulletins_complets}</span>
                        <span className="stat-label">Complets</span>
                      </div>
                      {statistiques.bulletins_partiels > 0 && (
                        <div className="stat-item warning">
                          <span className="stat-number">{statistiques.bulletins_partiels}</span>
                          <span className="stat-label">Partiels</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Filtres */}
              <div className="filters-card">
                <div className="card-header">
                  <h3>🔍 Rechercher une Classe</h3>
                  <p>Sélectionnez une classe et un trimestre pour voir les bulletins</p>
                </div>
                <div className="filters-grid">
                  <div className="filter-group">
                    <label>📚 Classe</label>
                    <select 
                      value={filtres.classe} 
                      onChange={(e) => setFiltres({...filtres, classe: e.target.value})}
                    >
                      <option value="">Sélectionner une classe</option>
                      <option value="6ème">6ème</option>
                      <option value="5ème">5ème</option>
                      <option value="4ème">4ème</option>
                      <option value="3ème">3ème</option>
                      <option value="Seconde A">Seconde A</option>
                      <option value="Première">Première</option>
                      <option value="Terminale">Terminale</option>
                    </select>
                  </div>

                  <div className="filter-group">
                    <label>📅 Trimestre</label>
                    <select 
                      value={filtres.trimestre} 
                      onChange={(e) => setFiltres({...filtres, trimestre: e.target.value})}
                    >
                      <option value="">Sélectionner un trimestre</option>
                      <option value="1">Trimestre 1</option>
                      <option value="2">Trimestre 2</option>
                      <option value="3">Trimestre 3</option>
                    </select>
                  </div>

                  <div className="filter-group">
                    <label>📊 Année scolaire</label>
                    <select 
                      value={filtres.annee_scolaire} 
                      onChange={(e) => setFiltres({...filtres, annee_scolaire: e.target.value})}
                    >
                      <option value="2025-2026">2025-2026</option>
                      <option value="2024-2025">2024-2025</option>
                    </select>
                  </div>
                </div>

                {statistiques && statistiques.peut_generer_classe && (
                  <div className="actions-section">
                    <button 
                      className="btn btn-primary btn-generate"
                      onClick={genererTousLesBulletins}
                    >
                      <span className="btn-icon">⚡</span>
                      <span>Générer tous les bulletins de la classe</span>
                    </button>
                    <small className="help-text success">
                      ✅ Tous les élèves ont leurs notes complètes!
                    </small>
                  </div>
                )}
              </div>

              {/* Liste des bulletins */}
              <div className="bulletins-card">
                <div className="card-header">
                  <h3>📄 Bulletins des Élèves</h3>
                  <p>Cliquez sur un élève pour voir le détail de son bulletin</p>
                </div>
                
                {loading ? (
                  <div className="loading-state">
                    <div className="spinner"></div>
                    <h4>Chargement des bulletins...</h4>
                  </div>
                ) : !filtres.classe || !filtres.trimestre ? (
                  <div className="empty-state">
                    <div className="empty-icon">🔍</div>
                    <h3>Sélectionnez une classe et un trimestre</h3>
                    <p>Utilisez les filtres ci-dessus pour afficher les bulletins</p>
                  </div>
                ) : bulletins.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-icon">📄</div>
                    <h3>Aucun bulletin trouvé</h3>
                    <p>Aucun élève n'a de notes validées pour cette classe et ce trimestre.</p>
                  </div>
                ) : (
                  <div className="bulletins-list">
                    {bulletins.map((bulletin, index) => (
                      <div 
                        key={index} 
                        className={`bulletin-card ${bulletin.est_complet ? 'complet' : 'partiel'}`}
                        onClick={() => ouvrirModal(bulletin)}
                      >
                        <div className="bulletin-header">
                          <div className="student-info">
                            <h4>{bulletin.etudiant.nom_complet}</h4>
                            <span className="matricule">{bulletin.etudiant.matricule}</span>
                          </div>
                          {getStatutBadge(bulletin)}
                        </div>
                        <div className="bulletin-stats">
                          <div className="stat">
                            <span className="stat-label">Moyenne:</span>
                            <span className={`stat-value ${bulletin.moyenne_generale >= 10 ? 'good' : 'bad'}`}>
                              {bulletin.moyenne_generale}/20
                            </span>
                          </div>
                          <div className="stat">
                            <span className="stat-label">Matières:</span>
                            <span className="stat-value">
                              {bulletin.nombre_matieres_validees}/{bulletin.nombre_matieres_attendues}
                            </span>
                          </div>
                        </div>
                        {!bulletin.est_complet && (
                          <div className="bulletin-warning">
                            ⚠️ {bulletin.matieres_manquantes.length} matière(s) manquante(s)
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Modal de détail du bulletin */}
      {showModal && selectedBulletin && (
        <div className="modal-overlay" onClick={fermerModal}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h3>📋 Bulletin de {selectedBulletin.etudiant.nom_complet}</h3>
                <p className="modal-subtitle">
                  {selectedBulletin.etudiant.classe} - Trimestre {selectedBulletin.trimestre} - {selectedBulletin.annee_scolaire}
                </p>
              </div>
              <button className="close-btn" onClick={fermerModal}>×</button>
            </div>
            
            <div className="modal-body">
              {/* Moyenne générale */}
              <div className="moyenne-generale-card">
                <h4>Moyenne Générale</h4>
                <div className={`moyenne-value ${selectedBulletin.moyenne_generale >= 10 ? 'good' : 'bad'}`}>
                  {selectedBulletin.moyenne_generale}/20
                </div>
                <p className="coefficients">Coefficients: {selectedBulletin.total_coefficients}</p>
              </div>

              {/* Notes validées */}
              {selectedBulletin.matieres.length > 0 && (
                <div className="notes-section">
                  <h4>✅ Notes Validées ({selectedBulletin.matieres.length})</h4>
                  <table className="notes-table">
                    <thead>
                      <tr>
                        <th>Matière</th>
                        <th>Moyenne</th>
                        <th>Coef.</th>
                        <th>Notes</th>
                        <th>Professeur</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedBulletin.matieres.map((matiere, idx) => (
                        <tr key={idx}>
                          <td className="matiere-name">{matiere.matiere}</td>
                          <td className={`moyenne ${matiere.moyenne >= 10 ? 'good' : 'bad'}`}>
                            {matiere.moyenne}/20
                          </td>
                          <td>{matiere.coefficient}</td>
                          <td>{matiere.nombre_notes}</td>
                          <td className="prof-name">{matiere.enseignant}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Matières manquantes */}
              {selectedBulletin.matieres_manquantes.length > 0 && (
                <div className="matieres-manquantes-section">
                  <h4>⚠️ Matières Manquantes ({selectedBulletin.matieres_manquantes.length})</h4>
                  <div className="matieres-manquantes-list">
                    {selectedBulletin.professeurs_matieres_manquantes.map((item, idx) => (
                      <div key={idx} className="matiere-manquante-card">
                        <div className="matiere-info">
                          <span className="matiere-name">{item.matiere}</span>
                          <span className="prof-name">👨‍🏫 {item.enseignant}</span>
                        </div>
                        <span className="status-badge">En attente</span>
                      </div>
                    ))}
                  </div>
                  <div className="help-message">
                    💡 Contactez les professeurs ci-dessus pour qu'ils saisissent et soumettent les notes manquantes.
                  </div>
                </div>
              )}
            </div>

            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={fermerModal}>
                Fermer
              </button>
              <button 
                className={`btn ${selectedBulletin.est_complet ? 'btn-success' : 'btn-disabled'}`}
                onClick={() => selectedBulletin.est_complet && genererBulletinIndividuel(selectedBulletin.etudiant.id)}
                disabled={!selectedBulletin.est_complet}
                title={selectedBulletin.est_complet ? 'Générer le bulletin' : 'Bulletin incomplet - notes manquantes'}
              >
                {selectedBulletin.est_complet ? '✅ Générer ce bulletin' : '⚠️ Bulletin partiel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GestionBulletins;
